import { DASHBOARD_REPORT_LABELS } from "./dashboardReportCatalog.js";
import { canShowReportCompartments } from "../lib/accessControl.js";

export const REPORT_EMAIL_TYPE = "reports.email";
export const REPORT_EMAIL_MAX_ITEMS = 30;
export const REPORT_EMAIL_LABELS = {
  meunerie: "Rapport Meunerie", truck_sheets: "Horaire chauffeur", truck_preloads: "Préchargements camions",
  orders: "Rapport Commandes", results: "Export détaillé", dispatch_gantt: "Dispatch visuel",
  ...DASHBOARD_REPORT_LABELS,
};

export const DASHBOARD_EMAIL_PRESETS = [
  { key: "overview", label: "Vue d’ensemble", dashboardKeys: ["tonnage", "utilization", "truck_average"] },
  { key: "loading_efficiency", label: "Efficacité du chargement", dashboardKeys: ["peak_load", "compartments", "truck_average"] },
  { key: "dispatch_organization", label: "Organisation du dispatch", dashboardKeys: ["occupation", "client_visits", "dispatch_structure"] },
];

export const DEFAULT_DASHBOARD_EMAIL_SETTINGS = {
  date_mode: "yesterday", fixed_date: null, dispatch_period: "day", period: "day",
};

function isValidDateInput(value) {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return parsed.getUTCFullYear() === year && parsed.getUTCMonth() === month - 1 && parsed.getUTCDate() === day;
}

export function defaultReportEmailItem(kind = "report", key = "meunerie", overrides = {}) {
  return { kind, key, date_mode: kind === "dashboard" ? "yesterday" : "today", period: "day", dispatch_period: "day",
    ...(["truck_sheets", "truck_preloads"].includes(key) ? { show_compartments: true } : {}), ...overrides };
}

export function applyReportEmailCompartmentAccess(action, access) {
  return { ...action, items: (action.items || []).map((item) =>
    item.kind === "report" && ["truck_sheets", "truck_preloads"].includes(item.key)
      ? { ...item, show_compartments: canShowReportCompartments(access, item.key) ? item.show_compartments !== false : true }
      : item) };
}

function dashboardCatalogKeys(catalog) {
  const entries = Array.isArray(catalog) ? catalog : catalog?.dashboards;
  if (Array.isArray(entries)) return entries.map((entry) => entry?.key).filter(Boolean);
  return Object.keys(DASHBOARD_REPORT_LABELS);
}

export function dashboardEmailSettingsFromItems(items = []) {
  const firstDashboard = (Array.isArray(items) ? items : []).find((item) => item?.kind === "dashboard");
  if (!firstDashboard) return { ...DEFAULT_DASHBOARD_EMAIL_SETTINGS };
  return {
    date_mode: firstDashboard.date_mode || DEFAULT_DASHBOARD_EMAIL_SETTINGS.date_mode,
    fixed_date: firstDashboard.date_mode === "fixed" ? firstDashboard.fixed_date || null : null,
    dispatch_period: firstDashboard.dispatch_period || DEFAULT_DASHBOARD_EMAIL_SETTINGS.dispatch_period,
    period: firstDashboard.period || DEFAULT_DASHBOARD_EMAIL_SETTINGS.period,
  };
}

export function dashboardEmailItemIdentity(item) {
  return JSON.stringify([
    item?.key || "",
    item?.date_mode || DEFAULT_DASHBOARD_EMAIL_SETTINGS.date_mode,
    item?.fixed_date || null,
    item?.dispatch_period || DEFAULT_DASHBOARD_EMAIL_SETTINGS.dispatch_period,
    item?.period || DEFAULT_DASHBOARD_EMAIL_SETTINGS.period,
  ]);
}

function dashboardEmailCommonSettings(settings) {
  const providedSettings = settings || {};
  const commonSettings = {
    ...DEFAULT_DASHBOARD_EMAIL_SETTINGS,
    ...providedSettings,
    fixed_date: providedSettings.fixed_date || null,
  };
  if (commonSettings.date_mode !== "fixed") commonSettings.fixed_date = null;
  return commonSettings;
}

export function dashboardEmailItemsToAppend(items = [], selectedKeys = [], settings = {}, catalog) {
  const existingItems = Array.isArray(items) ? items : [];
  const selected = new Set(Array.isArray(selectedKeys) ? selectedKeys : []);
  const commonSettings = dashboardEmailCommonSettings(settings);
  const existingIdentities = new Set(existingItems.map((item) => dashboardEmailItemIdentity(item)));
  const additions = [];
  for (const key of dashboardCatalogKeys(catalog)) {
    if (!selected.has(key)) continue;
    const item = defaultReportEmailItem("dashboard", key, commonSettings);
    const identity = dashboardEmailItemIdentity(item);
    if (existingIdentities.has(identity)) continue;
    additions.push(item);
    existingIdentities.add(identity);
  }
  return additions;
}

export function appendDashboardEmailItems(items = [], selectedKeys = [], settings = {}, catalog) {
  const existingItems = Array.isArray(items) ? items : [];
  const capacity = Math.max(0, REPORT_EMAIL_MAX_ITEMS - existingItems.length);
  return [...existingItems, ...dashboardEmailItemsToAppend(existingItems, selectedKeys, settings, catalog).slice(0, capacity)];
}

export function defaultReportEmailAction(items = [defaultReportEmailItem()]) {
  return { type: REPORT_EMAIL_TYPE, items, recipients: { user_ids: [], emails: [] },
    subject: items.length === 1 ? REPORT_EMAIL_LABELS[items[0].key] : "Rapports et indicateurs",
    message: "Bonjour,\n\nVoici les rapports et indicateurs demandés." };
}

export function serializeReportEmailAction(action) {
  const { _key, _emailsText, ...payload } = action;
  const emails = (_emailsText === undefined ? action.recipients?.emails || [] : _emailsText.split(/[;,\n]+/))
    .map((email) => email.trim().toLowerCase()).filter(Boolean);
  return { ...payload, subject: (action.subject || "").trim(), recipients: { user_ids: action.recipients?.user_ids || [], emails: [...new Set(emails)] },
    items: (action.items || []).map((item) => ({ ...item, fixed_date: item.date_mode === "fixed" ? item.fixed_date || null : null,
      date_to: item.date_mode === "fixed" && item.key === "orders" ? item.date_to || null : null,
      employee_id: item.employee_id || null })) };
}

export function reportEmailValidation(action) {
  const payload = serializeReportEmailAction(action);
  if (!payload.subject || payload.subject.length > 255 || /[\x00-\x1f]/.test(payload.subject)) return "Renseignez un objet valide (255 caractères maximum).";
  if (!payload.items.length || payload.items.length > REPORT_EMAIL_MAX_ITEMS) return `Sélectionnez entre 1 et ${REPORT_EMAIL_MAX_ITEMS} contenus.`;
  if (!payload.recipients.user_ids.length && !payload.recipients.emails.length) return "Sélectionnez un utilisateur ou ajoutez une adresse courriel.";
  if (payload.recipients.user_ids.length + payload.recipients.emails.length > 100) return "Un envoi accepte au maximum 100 destinataires.";
  if (payload.recipients.emails.some((email) => email.length > 254 || !/^[^\s@<>;,]+@[^\s@<>;,]+\.[^\s@<>;,]+$/.test(email))) return "Une adresse courriel est invalide.";
  for (const item of payload.items) {
    if (!REPORT_EMAIL_LABELS[item.key]) return "Un contenu sélectionné est inconnu.";
    if (item.date_mode === "fixed" && !isValidDateInput(item.fixed_date)) return "Choisissez une date fixe valide pour chaque contenu.";
    if (item.date_to && !isValidDateInput(item.date_to)) return "Choisissez une date de fin valide.";
    if (item.date_to && item.date_to < item.fixed_date) return "La fin de la période doit suivre son début.";
  }
  return "";
}
